import * as Yup from 'yup'
import { type CellOwnerAgreementDetails } from './CellOwnerAgreementDomainDtos'
import { type AssetOrderDetails } from '../assets/AssetOrderDomainDtos'
import { type LedgerAccountDetails } from '../financial/LedgerAccountDomainDtos'

export interface AccumulationCurrencyInstructionDetails {
  id: number
  assetHolderId: number
  projectId: number
  projectName: string
  projectStatus: string
  currencyId: number
  currencyUnit: string
  expiredAt: string | null
  supersededBy: number | undefined
}

export interface CharityContributionInstructionDetails {
  id: number
  assetHolderId: number
  projectId: number
  projectName: string
  projectStatus: string
  organisationId: number
  organisationName: string
  percentageDistribution: number
  expiredAt: string | null
  supersededBy: number | undefined
}

export interface LegacyKycResultDto {
  id: number
  assetHolderId: number
  provider: string
  tierLevel: string
  status: string
  kycDateTime: string
  narrative: string
  response: string

  manualCheckAtFormatted: string
  manualCheckByEmail: string
  manualCheckReferenceUrl: string
  manualCheckS3FolderName: string
}

export interface AssetHolderRole {
  id: number
  legacyRole: string
}

export interface UsCitizenStatusDto {
  isSelfDeclaredCitizen: boolean
  isUsCitizen: boolean
  isOnCitizenWhitelist: boolean
}

// + sync from back-end-v2/src/domains/asset_holding/api-schema-dtos/AssetHolderCrudDtos.ts
export interface AssetHolderUpdateDto {
  assetHolderId: number
  partyId: number
  legacyFirstName: string
  legacyLastName: string
  dateRegistered: string | null
  registrationState: string
  legacyDob: string | null
  legacyPhoneNumber: string | null
  legacyAddress: string | null
  legacyCity: string | null
  legacyPostalCode: string | null
  legacyCountry: string | null
  websiteUrl: string | null
  legacyEmailAddress: string
  legacyEarlyAccess: boolean
  twoFAStatus: boolean
  lastLoginAt: Date | null

  overrideLockRequested: boolean

  usCitizenStatus: UsCitizenStatusDto
  latestLegacyKycResult: LegacyKycResultDto | null
  assetHolderRoles: AssetHolderRole[]
  assetHolderKycResults: LegacyKycResultDto[]
  cellOwnerAgreements: CellOwnerAgreementDetails[]
  assetHolderOrders: AssetOrderDetails[]
  accumulationInstructions: AccumulationCurrencyInstructionDetails[]
  charityInstructions: CharityContributionInstructionDetails[]
  ledgerAccounts: LedgerAccountDetails[]

  assetHolderStatus: {
    loginEnabled: boolean
    statusReason: string | null
    notes: string
    accountClosed: boolean
    closedAtFormatted: string
    closeReason: string
  }
}

export const validateAssetHolderUpdateDto = (): any => {
  return Yup.object().shape({
    assetHolderId: Yup.number().required(),
    partyId: Yup.number().required(),
    legacyFirstName: Yup.string().required(),
    legacyLastName: Yup.string().required(),
    dateRegistered: Yup.string().nullable(true),
    legacyDob: Yup.string().nullable(true),
    websiteUrl: Yup.string().nullable(true),

    latestLegacyKycResult: Yup.object({
      id: Yup.number().required(),
      assetHolderId: Yup.number().required(),
      provider: Yup.string().required(),
      tierLevel: Yup.string().required(),
      status: Yup.string().required(),
      kycDateTime: Yup.string().required(),
      narrative: Yup.string().required(),
      response: Yup.string().required()
    })
  })
}

export interface AssetOrderFreeCellsPayload {
  assetHolderId: number
  assetId: number
  numberOfCells: number
  reference: string
  trelloLink: string
}

export const validateAssetOrderFreeCellsPayload = (): any => {
  return Yup.object().shape({
    assetHolderId: Yup.number().required(),
    assetId: Yup.number().required(),
    numberOfCells: Yup.number().required(),
    reference: Yup.string().required(),
    trelloLink: Yup.string().required()
  })
}
